/**
 * API client for communicating with the dashboard backend.
 */

import type { ApiResponse, TokenResponse } from '../types';

const BACKEND_ORIGIN_STORAGE_KEY = 'dashboard_backend_origin';
const ACCESS_TOKEN_STORAGE_KEY = 'access_token';
const REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';

function normalizeOrigin(raw: string): string {
  let u = raw.trim();
  if (!u) return '';
  if (!/^https?:\/\//i.test(u)) u = `http://${u}`;
  u = u.replace(/\/+$/, '');
  return u;
}

export function getBackendOrigin(): string | null {
  const raw = localStorage.getItem(BACKEND_ORIGIN_STORAGE_KEY);
  if (!raw) return null;
  const norm = normalizeOrigin(raw);
  return norm || null;
}

export function setBackendOrigin(origin: string | null) {
  if (!origin) {
    localStorage.removeItem(BACKEND_ORIGIN_STORAGE_KEY);
    return;
  }
  localStorage.setItem(BACKEND_ORIGIN_STORAGE_KEY, normalizeOrigin(origin));
}

function adaptUrlToBrowserHost(rawUrl: string): string {
  if (typeof window === 'undefined' || typeof (window as any).Capacitor !== 'undefined') {
    return rawUrl;
  }

  try {
    const parsed = new URL(rawUrl);
    parsed.protocol = window.location.protocol;
    parsed.hostname = window.location.hostname;
    return parsed.toString();
  } catch {
    return rawUrl;
  }
}

function resolveApiBaseUrl(): string {
  // 1) Runtime override (Android APK bundles the UI and points to a backend IP)
  const origin = getBackendOrigin();
  if (origin) return `${origin}/api/v1`;

  // 2) Explicit build-time override, adapted to the current browser host.
  const envUrl = import.meta.env.VITE_API_URL as string | undefined;
  if (envUrl) return adaptUrlToBrowserHost(envUrl);

  // 3) Default: same-origin web app (served behind reverse proxy)
  // NOTE: when running inside Capacitor without a configured backend, this won't work.
  return '/api/v1';
}

export const API_BASE_URL = resolveApiBaseUrl();

class ApiClient {
  private baseUrl: string;
  private accessToken: string | null = null;
  private refreshTokenValue: string | null = null;
  private refreshPromise: Promise<string | null> | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    this.accessToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    this.refreshTokenValue = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  }

  /**
   * Set/override the API base URL at runtime.
   * Useful for Capacitor builds where the UI is bundled in the APK.
   */
  setBaseUrl(baseUrl: string): void {
    this.baseUrl = baseUrl;
  }

  /**
   * Get current API base URL.
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Set the access token for authenticated requests.
   */
  setAccessToken(token: string | null): void {
    this.accessToken = token;
    if (token) {
      localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    }
  }

  setRefreshToken(token: string | null): void {
    this.refreshTokenValue = token;
    if (token) {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    }
  }

  /**
   * Get the current access token.
   */
  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshTokenValue;
  }

  /**
   * Check if user is authenticated.
   */
  isAuthenticated(): boolean {
    return this.accessToken !== null || this.refreshTokenValue !== null;
  }

  /**
   * Build headers for API requests.
   */
  private getHeaders(includeAuth: boolean = true): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (includeAuth && this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    return headers;
  }

  private async performRefresh(): Promise<string | null> {
    if (!this.refreshTokenValue) {
      return null;
    }

    if (!this.refreshPromise) {
      this.refreshPromise = (async () => {
        const response = await fetch(`${this.baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            refresh_token: this.refreshTokenValue,
          }),
        });

        if (!response.ok) {
          throw new Error('Refresh failed');
        }

        const data = await response.json();
        const accessToken = data.data?.access_token || null;
        if (!accessToken) {
          throw new Error('Refresh failed');
        }

        this.setAccessToken(accessToken);
        return accessToken;
      })().finally(() => {
        this.refreshPromise = null;
      });
    }

    return this.refreshPromise;
  }

  /**
   * Make an API request.
   */
  async request<T>(
    method: string,
    endpoint: string,
    options: {
      body?: unknown;
      params?: Record<string, string | number | boolean | string[]>;
      includeAuth?: boolean;
      retryOnUnauthorized?: boolean;
    } = {}
  ): Promise<ApiResponse<T>> {
    const { body, params, includeAuth = true, retryOnUnauthorized = true } = options;

    let url = `${this.baseUrl}${endpoint}`;

    // Add query parameters
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, v));
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const response = await fetch(url, {
      method,
      headers: this.getHeaders(includeAuth),
      body: body ? JSON.stringify(body) : undefined,
    });

    if (response.status === 401) {
      const canRefresh =
        includeAuth &&
        retryOnUnauthorized &&
        endpoint !== '/auth/refresh' &&
        this.refreshTokenValue;

      if (canRefresh) {
        try {
          const newAccessToken = await this.performRefresh();
          if (newAccessToken) {
            return this.request<T>(method, endpoint, {
              body,
              params,
              includeAuth,
              retryOnUnauthorized: false,
            });
          }
        } catch {
          // Fall through to logout behavior below.
        }
      }

      this.logout();
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      throw new Error('Unauthorized');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `API error: ${response.status}`);
    }

    return data;
  }

  /**
   * GET request helper.
   */
  async get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean | string[]>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('GET', endpoint, { params });
  }

  /**
   * POST request helper.
   */
  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('POST', endpoint, { body });
  }

  /**
   * PUT request helper.
   */
  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', endpoint, { body });
  }

  /**
   * PATCH request helper.
   */
  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', endpoint, { body });
  }

  /**
   * DELETE request helper.
   */
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', endpoint);
  }

  // Auth endpoints
  async login(username: string, password: string): Promise<TokenResponse> {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    const response = await fetch(`${this.baseUrl}/auth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }

    if (data.data?.access_token) {
      this.setAccessToken(data.data.access_token);
    }
    if (data.data?.refresh_token) {
      this.setRefreshToken(data.data.refresh_token);
    }

    return data.data;
  }

  logout(): void {
    this.setAccessToken(null);
    this.setRefreshToken(null);
  }

  async refreshToken(refreshToken?: string): Promise<TokenResponse> {
    const tokenToUse = refreshToken || this.refreshTokenValue;
    if (!tokenToUse) {
      throw new Error('No refresh token available');
    }

    const response = await this.post<TokenResponse>('/auth/refresh', {
      refresh_token: tokenToUse,
    });
    if (response.data.access_token) {
      this.setAccessToken(response.data.access_token);
    }
    if (tokenToUse !== this.refreshTokenValue) {
      this.setRefreshToken(tokenToUse);
    }
    return response.data;
  }

  async getCurrentUser() {
    return this.get('/auth/me');
  }
}

// Export singleton instance
export const api = new ApiClient();

// Export class for testing
export { ApiClient };
